import { Modal, message, Select, DatePicker, Button, Input } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { putRequest, getRequest } from "../../service";
import dayjs from "dayjs";
import { getChangedFields } from "../../utils/getChangedFields";

const ContractEdit = (props) => {
  const { open, onRequestClose, employee, onSuccess } = props;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    joining_date: "",
    employee_type: "",
    contract_end_date: "",
    shift: "",
    p_confirmation_date: "",
    location: "",
    department: "",
    designation: "",
    sub_department: "",
    reporting_to: "",
    status: "active",
    last_working_date: "",
    reason_for_leaving: null
  });
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const originalFormRef = useRef({});

  // Fetch data first
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchShifts(),
        fetchLocations(),
        fetchDesignations(),
        fetchDepartments(),
        fetchReportingManagers()
      ]);
    };
    fetchData();
  }, []);

  // Set form values after data is loaded
  useEffect(() => {
    if (employee) {
      // Find the matching shift ID from the shifts array
      const matchingShift = shifts.find(s => s.shiftName === employee.shift?.shiftName);
      
      // Find the matching department ID from the departments array
      const matchingDepartment = departments.find(d => d.name === employee.department?.name);
      
      // Find the matching designation ID from the designations array
      const matchingDesignation = designations.find(d => d.designationName === employee.designation?.designationName);

      const initialData = {
        joining_date: employee.joining_date || "",
        employee_type: employee.employee_type || "",
        contract_end_date: employee.contract_end_date || "",
        shift: matchingShift?._id || "",
        p_confirmation_date: employee.p_confirmation_date || "",
        location: employee.location?._id || "",
        department: matchingDepartment?._id || "",
        designation: matchingDesignation?._id || "",
        sub_department: employee.sub_department || "",
        reporting_to: employee.reporting_to || "",
        status: employee.status || "active",
        last_working_date: employee.last_working_date || "",
        reason_for_leaving: employee.reason_for_leaving || null
      };

      setForm(initialData);
      originalFormRef.current = initialData;

      // Set sub-departments if department exists
      if (employee.department?.subDepartments) {
        const subDeptOptions = employee.department.subDepartments.map(subDept => ({
          value: subDept._id,
          label: subDept.name
        }));
        setSubDepartments(subDeptOptions);
      }
    }
  }, [employee, shifts, locations, departments, designations, reportingManagers]);

  const fetchShifts = async () => {
    try {
      const response = await getRequest("/api/companies/shifts");
      if (response?.shifts) {
        setShifts(response.shifts);
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await getRequest("/api/companies/locations");
      if (response?.locations) {
        setLocations(response.locations);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await getRequest("/api/companies/designation");
      if (response?.designations) {
        setDesignations(response.designations);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await getRequest("/api/department");
      if (response?.departments) {
        setDepartments(response.departments);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchReportingManagers = async () => {
    try {
      const response = await getRequest("/api/companies/reportto");
      if (response?.employees) {
        setReportingManagers(response.employees);
      }
    } catch (error) {
      console.error("Error fetching reporting managers:", error);
    }
  };

  const handleSelectChange = (value, name) => {
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const effectiveForm = {
        ...form,
        ...(form.status === "inActive"
          ? {}
          : {
              last_working_date: null,
              reason_for_leaving: null,
            }),
      };

      const payload = getChangedFields(originalFormRef.current, effectiveForm);
      if (Object.keys(payload).length === 0) {
        message.info("No changes to update");
        return;
      }

      const response = await putRequest(`/api/employees?employeeId=${employee._id}`, payload);
      if (response.message === "Employee updated successfully (some related data could not be loaded)") {
        message.success("Employee updated successfully");
        onRequestClose();
        if (onSuccess) onSuccess();
      } else {
        message.error(response.message || "Employee not updated");
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      message.error('Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  // Helper to format time in 12-hour format with AM/PM
  const formatTime12Hour = (timeStr) => {
    if (!timeStr || timeStr === "-" || timeStr === "ss") return "-";
    const [hours, minutes] = timeStr.split(":");
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const shiftOptions = shifts.map(shift => ({
    value: shift._id,
    label: `${shift.shiftName} (${formatTime12Hour(shift.startTime)} to ${formatTime12Hour(shift.endTime)})`
  }));

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inActive", label: "Inactive" }
  ];

  const employmentTypeOptions = [
    { value: "internship", label: "Internship" },
    { value: "regular", label: "Regular" },
    { value: "contract", label: "Contract" }
  ];

  const workLocationOptions = locations.map(location => ({
    value: location._id,
    label: `${location.locationName} | ${location.city}`
  }));

  const departmentOptions = departments.map(dept => ({
    value: dept._id,
    label: dept.name
  }));

  const designationOptions = designations.map(designation => ({
    value: designation._id,
    label: designation.designationName
  }));

  const reportingToOptions = [
    { value: "", label: "None" },
    ...reportingManagers.map(manager => ({
      value: manager._id,
      label: `${manager.name} | ${manager.department || ''}`
    }))
  ];

  const reasonForLeavingOptions = [
    { value: 'layOff', label: 'Lay Off' },
    { value: 'termination', label: 'Termination' },
    { value: 'resigned', label: 'Resigned' }
  ];

  return (
    <Modal
      title="Contract Information"
      open={open}
      onCancel={onRequestClose}
      footer={null}
      width={800}
    >
      <div className="flex items-start justify-between bg-white h-[55vh] overflow-auto">
        <div className="bg-form-bg w-full rounded-md px-4 py-8">
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-6 flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <label htmlFor="joining_date" className="text-md">
                  Joining Date <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <DatePicker
                  onChange={(_date, dateString) => handleSelectChange(dateString, "joining_date")}
                  placeholder="YYYY/MM/DD"
                  value={form.joining_date ? dayjs(form.joining_date) : null}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="employee_type" className="text-md">
                  Employment Type <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <Select
                  options={employmentTypeOptions}
                  value={form.employee_type}
                  onChange={(value) => handleSelectChange(value, "employee_type")}
                  className="w-full"
                />
              </div>

              {form.employee_type === "contract" && (
                <div className="flex flex-col gap-1">
                  <label htmlFor="contract_end_date" className="text-md">
                    Contract End Date <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                  </label>
                  <DatePicker
                    onChange={(_date, dateString) => handleSelectChange(dateString, "contract_end_date")}
                    placeholder="YYYY/MM/DD"
                    value={form.contract_end_date ? dayjs(form.contract_end_date) : null}
                  />
                </div>
              )}
            </div>

            <div className="col-span-6 flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <label htmlFor="shift" className="text-md">
                  Shift <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <Select
                  options={shiftOptions}
                  value={form.shift}
                  onChange={(value) => handleSelectChange(value, "shift")}
                  className="w-full"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="p_confirmation_date" className="text-md">
                  Permanent Confirmation Date <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                </label>
                <DatePicker
                  onChange={(_date, dateString) => handleSelectChange(dateString, "p_confirmation_date")}
                  placeholder="YYYY/MM/DD"
                  value={form.p_confirmation_date ? dayjs(form.p_confirmation_date) : null}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="flex flex-col gap-1">
              <label htmlFor="work_location" className="text-md">
                Work Location <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                options={workLocationOptions}
                value={form.location}
                onChange={(value) => handleSelectChange(value, "location")}
                className="w-full"
                placeholder="Select Work Location"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="department" className="text-md">
                Department <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                options={departmentOptions}
                value={form.department}
                onChange={(value) => handleSelectChange(value, "department")}
                className="w-full"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="designation" className="text-md">
                Designation <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                options={designationOptions}
                value={form.designation}
                onChange={(value) => handleSelectChange(value, "designation")}
                className="w-full"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="reporting_to" className="text-md">
                Reporting To 
              </label>
              <Select
                options={reportingToOptions}
                value={form.reporting_to}
                onChange={(value) => handleSelectChange(value ?? "", "reporting_to")}
                className="w-full"
                allowClear
                placeholder="None"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="status" className="text-md">
                Status <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
              </label>
              <Select
                options={statusOptions}
                value={form.status}
                onChange={(value) => handleSelectChange(value, "status")}
                className="w-full"
              />
            </div>

            {/* Additional fields shown when status is inActive */}
            {form.status === "inActive" && (
              <>
                <div className="flex flex-col gap-1">
                  <label htmlFor="last_working_date" className="text-md">
                    Last Working Date <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                  </label>
                  <DatePicker
                    onChange={(_date, dateString) => handleSelectChange(dateString, "last_working_date")}
                    placeholder="YYYY/MM/DD"
                    value={form.last_working_date ? dayjs(form.last_working_date) : null}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="reason_for_leaving" className="text-md">
                    Reason for Leaving <span className="text-red-500 relative -top-0.5 ml-0.5">*</span>
                  </label>
                  <Select
                    options={reasonForLeavingOptions}
                    value={form.reason_for_leaving}
                    onChange={(value) => handleSelectChange(value, "reason_for_leaving")}
                    className="w-full"
                    placeholder="Select reason"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 my-2">
        <Button onClick={onRequestClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          type="primary"
          className="bg-primary text-white"
          loading={loading}
          disabled={loading}
        >
          Update Contract
        </Button>
      </div>
    </Modal>
  );
};

export default ContractEdit;